import { isUndefined } from 'lodash';
import { AccountDocumentUriConstant } from 'src/constants';
import { AccountApplicationCategory, EntityAccountType } from 'src/models';

import { Maybe } from './types.utils';

export const getAccountApplicationCategoryDescription = ({
  entityAccountType,
  category,
}: {
  entityAccountType?: EntityAccountType;
  category: AccountApplicationCategory;
}): Maybe<string> => {
  if (category.name.isApplication) {
    if (entityAccountType?.isCorporate) {
      return 'Since you requested to open a Corporate Account, please download the below Corporate Account Application and then Upload once you have signed and completed the application.';
    }

    if (entityAccountType?.isLlc) {
      const filingTaxType = entityAccountType.isLlcCCorporate
        ? 'C-Corporation'
        : entityAccountType.isLlcSCorporate
        ? 'S-Corporation'
        : 'Partnership';

      return `Since you requested to open a LLC Account filing taxes as a ${filingTaxType}, please download the below LLC Account Application and then Upload once you have signed and completed the application.`;
    }

    if (entityAccountType?.isPartnership) {
      return 'Since you requested to open a Partnership Account, please download the below Partnership Account Application and then Upload once you have signed and completed the application.';
    }

    if (entityAccountType?.isTrust) {
      const trustType = entityAccountType.isPersonalTrust ? 'Personal Trust' : 'Business Trust';

      return `Since you requested to open a ${trustType} Account, please download the below ${trustType} Account Application and then Upload once you have signed and completed the application.`;
    }
  }

  if (category.name.isArticlesOfIncorporation) {
    return 'Please upload a copy of your Certified Articles of Incorporation to process your application.';
  }

  if (category.name.isArticlesOfOrganization) {
    return 'Please upload a copy of your Certified Articles of Organization to process your application.';
  }

  if (category.name.isOperatingAgreement) {
    return 'Please upload a copy of your Operating Agreement to process your application.';
  }

  if (category.name.isPartnershipAgreement) {
    return 'Please upload a copy of your Partnership Agreement to process your application.';
  }

  if (category.name.isTrustAgreement) {
    return 'Please upload a copy of your Trust Agreement to process your application.';
  }

  if (category.name.isGovernmentId) {
    let ownerType;

    if (entityAccountType?.isCorporate) {
      ownerType = 'officers/beneficial owners';
    }

    if (entityAccountType?.isLlc) {
      ownerType = 'manager/member/beneficial owners';
    }

    if (entityAccountType?.isPartnership) {
      ownerType = 'general partners';
    }

    if (entityAccountType?.isTrust) {
      ownerType = 'trustees';
    }

    if (ownerType) {
      return `Please Upload a copy of government issued photo ID for all ${ownerType} (preferably your driver's license, state ID or passport).`;
    }
  }
};

export const getAccountApplicationCategorySampleDocument = ({
  entityAccountType,
  category,
}: {
  entityAccountType?: EntityAccountType;
  category: AccountApplicationCategory;
}): Maybe<{
  text: string;
  link: string;
}> => {
  if (category.name.isApplication) {
    if (entityAccountType?.isCorporate) {
      return {
        text: `Click here to download Corporation Account Application Pack (PDF)`,
        link: AccountDocumentUriConstant.CORPORATATE_FORM,
      };
    }

    if (entityAccountType?.isLlc) {
      return {
        text: `Click here to download LLC Corporation Account Application Pack (PDF)`,
        link: AccountDocumentUriConstant.LLC_FORM,
      };
    }

    if (entityAccountType?.isPartnership) {
      return {
        text: 'Click here to download Partnership Account Application Pack (PDF)',
        link: AccountDocumentUriConstant.PARTNERSHIP_FORM,
      };
    }

    if (entityAccountType?.isPersonalTrust) {
      return {
        text: 'Click here to download Personal Trust Account Application Pack (PDF)',
        link: AccountDocumentUriConstant.PERSONAL_TRUST_FORM,
      };
    }

    if (entityAccountType?.isBusinessTrust) {
      return {
        text: 'Click here to download Business Trust Account Application Pack (PDF)',
        link: AccountDocumentUriConstant.BUSINESS_TRUST_FORM,
      };
    }
  }
};

export const getAccountApplicationGovernmentIdCategoryTitle = ({
  entityAccountType,
  index,
}: {
  entityAccountType?: EntityAccountType;
  index?: number;
}): Maybe<string> => {
  let result;

  if (entityAccountType?.isCorporate) {
    result = `Officer/Beneficial Owner`;
  }

  if (entityAccountType?.isLlc) {
    result = `Manager/Member/Beneficial Owner`;
  }

  if (entityAccountType?.isPartnership) {
    result = `Partner`;
  }

  if (entityAccountType?.isTrust) {
    result = `Trustee`;
  }

  if (!isUndefined(index)) {
    result = `${result} #${index + 1}`;
  }

  return result;
};
